import styles from './ReportButton.module.scss';

import { RadioButtonChecked } from '@mui/icons-material';
import DescriptionIcon from '@mui/icons-material/Description';
import {
  Button,
  Fab,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  Theme,
  useMediaQuery,
} from '@mui/material';
import { useCallback, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { useDialogs } from '@work4all/components';
import { useReportOptions } from '@work4all/components/lib/components/entity-picker/report-picker/ReportPicker';
import { ReportType } from '@work4all/components/lib/components/entity-picker/report-type-picker/ReportTypePicker';
import { useReport2Options } from '@work4all/components/lib/components/entity-picker/report2-picker/Report2Picker';
import { Divider } from '@work4all/components/lib/dataDisplay/divider/Divider';

import { usePopoverState } from '@work4all/data/lib/hooks/usePopoverState';

import { BusinessPartner } from '@work4all/models/lib/Classes/BusinessPartner.entity';
import { Customer } from '@work4all/models/lib/Classes/Customer.entity';
import { ERPTypes } from '@work4all/models/lib/Classes/ERPTypes.entity';
import { Position } from '@work4all/models/lib/Classes/Position.entity';
import { Report } from '@work4all/models/lib/Classes/Report.entity';
import { Report2 } from '@work4all/models/lib/Classes/Report2.entity';
import { EInvoiceFormat } from '@work4all/models/lib/Enums/EInvoiceFormat.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';
import { InvoiceForm } from '@work4all/models/lib/Enums/InvoiceForm.enum';
import { SdObjType } from '@work4all/models/lib/Enums/SdObjType.enum';
import { convertEntityToBzObjType } from '@work4all/models/lib/utils/convertEntityToBzObjType';

import { settings, useSetting } from '../../../../../../../../settings';
import { useFormContextPlus } from '../../../../../../form-plus/use-form-context-plus';
import { useMaskContext } from '../../../../../hooks/mask-context';
import { ErpData } from '../../../ErpData';
import { convertEntityToReportBzObjType } from '../../../hooks/use-bz-shadow-object-api/use-shadow-bz-object-api';
import { useShadowBzObjectApiContext } from '../../../hooks/use-bz-shadow-object-api/use-shadow-bz-object-api-context';

import { ReportPreviewMask } from './ReportPreviewMask';

interface ReportButtonProps {
  disabled?: boolean;
}

export function ReportButton(props: ReportButtonProps) {
  const { disabled } = props;
  const { t } = useTranslation();
  const mask = useMaskContext();
  const form = useFormContextPlus<ErpData>();

  const { persist, isDirty, positions } = useShadowBzObjectApiContext();

  const { pathname } = useLocation();
  const navigate = useNavigate();

  const invoiceAddress = form.watch('additionalAddress2');

  const isXInvoice = useMemo(() => {
    const customer: Customer = (
      invoiceAddress?.businessPartner as BusinessPartner
    )?.data;

    return (
      mask.entity === Entities.invoice &&
      customer?.invoiceForm === InvoiceForm.ELEKTRONISCH &&
      [EInvoiceFormat.X_RECHNUNG_CII, EInvoiceFormat.X_RECHNUNG_UBL].includes(
        customer?.eInvoiceFormat
      )
    );
  }, [invoiceAddress, mask.entity]);

  const reportBzObjectType = useMemo(() => {
    try {
      return convertEntityToReportBzObjType(mask.entity);
    } catch {
      return undefined;
    }
  }, [mask.entity]);

  const bzObjectType = useMemo(() => {
    try {
      return convertEntityToBzObjType(mask.entity);
    } catch {
      return undefined;
    }
  }, [mask.entity]);

  const { set: setLastUsedReportType } = useSetting(
    settings.lastUsedReportType()
  );

  const { data: reportList } = useReportOptions(reportBzObjectType);
  const { data: report2List } = useReport2Options(reportBzObjectType);

  const sortedReportList = useMemo(() => {
    return [...reportList].sort((a) => {
      return a.standardReport ? -1 : 1;
    });
  }, [reportList]);

  const dialogs = useDialogs();

  const isMdDown = useMediaQuery<Theme>((t) => t.breakpoints.down('md'));

  const buttonRef = useRef(null);
  const popoverState = usePopoverState('bottom');

  const handleClick = useCallback(
    async (props: { reportType: ReportType; report?: Report | Report2 }) => {
      // Persist the shadow object first if there are changes.
      if (isDirty || mask.isCreateMode) {
        await form.handleSubmit(async () => {
          const response = await persist();
          const data = response?.data?.persistShadowBzObject?.data;
          if (!data) {
            console.warn(
              'No data returned from persistShadowBzObject',
              response
            );
            return;
          }

          form.reset(data);

          setTimeout(() => {
            if (pathname.includes('/new') && data?.id) {
              const path = pathname.replace('new', `${data.id}`);
              navigate(path, { replace: true });
            }

            openReportMaskWithData({
              data: {
                ...data,
                positionList: (data?.positionList || []).filter(
                  (x) => 'posId' in x && !x.posId
                ),
              },
              ...props,
            });
          }, 100);
        })();
      } else {
        const data: ERPTypes = {
          ...form.getValues(),
          positionList: positions?.filter((x) => !(x as Position).posId) || [],
        };

        openReportMaskWithData({ data, ...props });
      }

      function openReportMaskWithData(props: {
        data: ERPTypes;
        reportType: ReportType;
        report?: Report | Report2;
      }) {
        const { data, reportType, report } = props;

        const contact = data.businessPartnerContactCombined.contact;
        const businessPartner =
          data.businessPartnerContactCombined.businessPartner;
        const businessPartnerType = data.businessPartnerType;

        const id = data.id ?? mask.id;

        setLastUsedReportType(reportType);

        // This is special - for now there is no reason to use MaskDialogProvider as it's not Entity Mask
        dialogs.overlay(
          <ReportPreviewMask
            id={id}
            reportBzObjectType={reportBzObjectType}
            report={report}
            reportType={reportType}
            data={data}
            contactId={contact?.id}
            businessPartnerId={businessPartner?.id}
            businessPartnerType={
              businessPartnerType === SdObjType.KUNDE
                ? Entities.customer
                : Entities.supplier
            }
            bzObjectType={bzObjectType}
            entity={mask.entity}
          />
        );
      }
    },
    [
      bzObjectType,
      dialogs,
      form,
      isDirty,
      mask.entity,
      mask.id,
      mask.isCreateMode,
      navigate,
      pathname,
      persist,
      positions,
      reportBzObjectType,
      setLastUsedReportType,
    ]
  );

  const handleButtonClick = useCallback(
    (e) => {
      if (isXInvoice) {
        handleClick({ reportType: null });
      } else {
        popoverState.handleClick(e);
      }
    },
    [handleClick, isXInvoice, popoverState]
  );

  return (
    <>
      <Popover anchorEl={buttonRef.current} {...popoverState}>
        <List>
          <ListItem disablePadding>
            <ListItemButton
              className={styles.listItemButton}
              onClick={() => {
                handleClick({ reportType: ReportType.Simple });
                popoverState.onClose();
              }}
            >
              <ListItemText>
                {t('COMMON.REPORT_TYPE.' + ReportType.Simple)}
              </ListItemText>
            </ListItemButton>
          </ListItem>
          {report2List?.length > 0 && (
            <>
              <Divider
                title={t('COMMON.REPORT_TYPE.' + ReportType.DevExpress)}
                horizontalPadding
              />
              {report2List.map((report) => {
                return (
                  <ListItem disablePadding key={report.id}>
                    <ListItemButton
                      className={styles.listItemButton}
                      onClick={() => {
                        handleClick({
                          reportType: ReportType.DevExpress,
                          report,
                        });
                        popoverState.onClose();
                      }}
                    >
                      <ListItemText>{report.name}</ListItemText>
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </>
          )}
          <Divider
            title={t('COMMON.REPORT_TYPE.' + ReportType.CrystalReports)}
            horizontalPadding
          />

          {sortedReportList.map((report) => {
            return (
              <ListItem
                key={report.id}
                disablePadding
                secondaryAction={
                  report.standardReport ? (
                    <IconButton edge="end" aria-label="delete">
                      <RadioButtonChecked className={styles.checkedIcon} />
                    </IconButton>
                  ) : null
                }
              >
                <ListItemButton
                  className={styles.listItemButton}
                  onClick={() => {
                    handleClick({
                      reportType: ReportType.CrystalReports,
                      report,
                    });
                    popoverState.onClose();
                  }}
                >
                  <ListItemText>{report.note}</ListItemText>
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Popover>
      {isMdDown ? (
        <Fab
          color="primary"
          size="medium"
          data-test-id={'preview-icon-button'}
          onClick={handleButtonClick}
          ref={buttonRef}
          disabled={disabled}
        >
          <DescriptionIcon />
        </Fab>
      ) : (
        <Button
          disabled={disabled}
          onClick={handleButtonClick}
          startIcon={<DescriptionIcon />}
          ref={buttonRef}
        >
          {t('COMMON.PREVIEW')}
        </Button>
      )}
    </>
  );
}
